import React, { useEffect, useState } from "react";
import { Edit, Eye, Plus, Search, Trash2, X } from "lucide-react";
import ConfirmationModal from "../../components/ConfirmationModal";
import apiService from "../../services/api";
import TablePagination from "../company/JobManagement/Components/TablePagination";

type SettingType = "text" | "html" | "json" | "boolean" | "number";

interface SystemSetting {
  setting_key: string;
  setting_name: string;
  description?: string;
  setting_type: SettingType;
  setting_value: unknown;
  is_public: boolean;
}

interface SettingFormState {
  setting_key: string;
  setting_name: string;
  description: string;
  setting_type: SettingType;
  setting_value: string;
  is_public: boolean;
}

type SettingValueDrafts = Partial<Record<SettingType, string>>;

const settingTypes: SettingType[] = [
  "text",
  "html",
  "json",
  "boolean",
  "number",
];

const emptySettingForm: SettingFormState = {
  setting_key: "",
  setting_name: "",
  description: "",
  setting_type: "text",
  setting_value: "",
  is_public: false,
};

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState("terms");
  const [termsContent, setTermsContent] = useState("");
  const [privacyContent, setPrivacyContent] = useState("");
  const [aboutContent, setAboutContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [systemConstants, setSystemConstants] = useState<SystemSetting[]>([]);
  const [constantsLoading, setConstantsLoading] = useState(false);
  const [constantsSaving, setConstantsSaving] = useState(false);
  const [showConstantModal, setShowConstantModal] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [constantForm, setConstantForm] =
    useState<SettingFormState>(emptySettingForm);
  const [constantValueDrafts, setConstantValueDrafts] =
    useState<SettingValueDrafts>({});
  const [constantsError, setConstantsError] = useState<string | null>(null);
  const [constantFormError, setConstantFormError] = useState<string | null>(
    null
  );
  const [viewingSetting, setViewingSetting] = useState<SystemSetting | null>(
    null
  );
  const [deleteTarget, setDeleteTarget] = useState<SystemSetting | null>(null);
  const [systemSearchInput, setSystemSearchInput] = useState("");
  const [systemSearchTerm, setSystemSearchTerm] = useState("");
  const [systemTypeFilter, setSystemTypeFilter] = useState("");
  const [systemVisibilityFilter, setSystemVisibilityFilter] = useState("");
  const [systemCurrentPage, setSystemCurrentPage] = useState(1);
  const [systemRowsPerPage, setSystemRowsPerPage] = useState(5);
  const [systemTotalCount, setSystemTotalCount] = useState(0);

  const tabs = [
    { id: "terms", name: "Terms & Conditions", icon: "📄" },
    { id: "privacy", name: "Privacy Policy", icon: "🔒" },
    { id: "about", name: "About Content", icon: "ℹ️" },
    { id: "system", name: "System Settings", icon: "⚙️" },
    { id: "email", name: "Email Templates", icon: "📧" },
    { id: "integrations", name: "Integrations", icon: "🔗" },
  ];

  // Fetch content when component mounts or tab changes
  useEffect(() => {
    if (
      activeTab === "terms" ||
      activeTab === "privacy" ||
      activeTab === "about"
    ) {
      fetchContent();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "system") {
      loadSystemConstants();
    }
  }, [
    activeTab,
    systemCurrentPage,
    systemRowsPerPage,
    systemSearchTerm,
    systemTypeFilter,
    systemVisibilityFilter,
  ]);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const settingKey =
        activeTab === "terms"
          ? "terms_content"
          : activeTab === "privacy"
          ? "privacy_content"
          : "about_content";

      // First try to get from admin endpoint (with auth via apiService)
      try {
        const response = await apiService.get(`/system-settings/${settingKey}`);
        const value = response.data?.setting_value || "";
        if (activeTab === "terms") {
          setTermsContent(value);
        } else if (activeTab === "privacy") {
          setPrivacyContent(value);
        } else {
          setAboutContent(value);
        }
        return;
      } catch (error: any) {
        console.log("Admin endpoint failed, trying public endpoint", error);
      }

      // Fallback to public endpoint
      try {
        const publicResponse = await apiService.get(
          `/system-settings/public/${settingKey}`
        );
        const value = publicResponse.data?.setting_value || "";
        if (activeTab === "terms") {
          setTermsContent(value);
        } else if (activeTab === "privacy") {
          setPrivacyContent(value);
        } else {
          setAboutContent(value);
        }
      } catch (publicError: any) {
        console.log("Public endpoint failed or no setting found", publicError);
        if (activeTab === "terms") {
          setTermsContent("");
        } else if (activeTab === "privacy") {
          setPrivacyContent("");
        } else {
          setAboutContent("");
        }
      }
    } catch (error) {
      console.error("Error fetching content:", error);
      setMessage({ type: "error", text: "Failed to load content" });
    } finally {
      setLoading(false);
    }
  };

  const saveContent = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const content =
        activeTab === "terms"
          ? termsContent
          : activeTab === "privacy"
          ? privacyContent
          : aboutContent;
      const settingKey =
        activeTab === "terms"
          ? "terms_content"
          : activeTab === "privacy"
          ? "privacy_content"
          : "about_content";

      await apiService.put(`/system-settings/${settingKey}`, {
        setting_value: content,
      });

      setMessage({
        type: "success",
        text:
          activeTab === "terms"
            ? "Terms & Conditions updated successfully"
            : activeTab === "privacy"
            ? "Privacy Policy updated successfully"
            : "About content updated successfully",
      });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error("Error saving content:", error);
      setMessage({
        type: "error",
        text: error instanceof Error ? error.message : "Failed to save content",
      });
    } finally {
      setSaving(false);
    }
  };

  const getFiniteNumber = (...values: unknown[]) => {
    for (const value of values) {
      if (value === null || value === undefined || value === "") {
        continue;
      }

      const numericValue = Number(value);

      if (Number.isFinite(numericValue)) {
        return numericValue;
      }
    }

    return null;
  };

  const getBooleanValue = (value: unknown) => {
    if (typeof value === "boolean") {
      return value;
    }

    if (typeof value === "string") {
      return value.toLowerCase() === "true";
    }

    return null;
  };

  const loadSystemConstants = async () => {
    setConstantsLoading(true);
    setConstantsError(null);

    try {
      const offset = (systemCurrentPage - 1) * systemRowsPerPage;
      const params: Record<string, string | number | boolean> = {
        offset,
        limit: systemRowsPerPage,
      };
      const trimmedSearch = systemSearchTerm.trim();

      if (trimmedSearch) {
        params.search = trimmedSearch;
      }

      if (systemTypeFilter) {
        params.setting_type = systemTypeFilter;
      }

      if (systemVisibilityFilter) {
        params.is_public = systemVisibilityFilter === "public";
      }

      const response = await apiService.get("/system-settings/", params);
      const data = response.data;
      const settings = Array.isArray(data)
        ? data
        : Array.isArray(data?.settings)
        ? data.settings
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.data)
        ? data.data
        : [];
      const hasMore =
        getBooleanValue(response.headers["x-has-more"]) ??
        getBooleanValue(data?.has_more ?? data?.hasMore) ??
        false;
      const total =
        getFiniteNumber(
          response.headers["x-total-count"],
          data?.total_count,
          data?.total,
          data?.count
        ) ??
        offset + settings.length + (hasMore ? 1 : 0);
      const totalPages = Math.max(
        1,
        Math.ceil(total / systemRowsPerPage)
      );

      setSystemConstants(settings as SystemSetting[]);
      setSystemTotalCount(total);

      if (systemCurrentPage > totalPages) {
        setSystemCurrentPage(totalPages);
      }
    } catch (error) {
      console.error("Error loading system constants:", error);
      setConstantsError(
        error instanceof Error
          ? error.message
          : "Failed to load system constants"
      );
      setSystemConstants([]);
      setSystemTotalCount(0);
    } finally {
      setConstantsLoading(false);
    }
  };

  const valueToFormString = (value: unknown, settingType: SettingType) => {
    if (settingType === "boolean") {
      return getBooleanValue(value) ? "true" : "false";
    }

    if (settingType === "json" && typeof value !== "string") {
      return JSON.stringify(value ?? {}, null, 2);
    }

    return value == null ? "" : String(value);
  };

  const getDefaultValueForType = (settingType: SettingType) => {
    if (settingType === "boolean") {
      return "false";
    }

    if (settingType === "json") {
      return "{}";
    }

    return "";
  };

  const updateConstantValue = (value: string) => {
    setConstantForm((current) => ({
      ...current,
      setting_value: value,
    }));
    setConstantValueDrafts((current) => ({
      ...current,
      [constantForm.setting_type]: value,
    }));
  };

  const hydrateConstantForm = (setting: SystemSetting) => {
    const settingType = setting.setting_type || "text";
    const settingValue = valueToFormString(setting.setting_value, settingType);

    setConstantForm({
      setting_key: setting.setting_key || "",
      setting_name: setting.setting_name || "",
      description: setting.description || "",
      setting_type: settingType,
      setting_value: settingValue,
      is_public: Boolean(setting.is_public),
    });
    setConstantValueDrafts({ [settingType]: settingValue });
  };

  const openAddConstant = () => {
    setEditingKey(null);
    setConstantForm(emptySettingForm);
    setConstantValueDrafts({
      [emptySettingForm.setting_type]: emptySettingForm.setting_value,
    });
    setConstantFormError(null);
    setShowConstantModal(true);
  };

  const openEditConstant = async (setting: SystemSetting) => {
    setEditingKey(setting.setting_key);
    setConstantFormError(null);
    setShowConstantModal(true);
    setConstantsSaving(true);

    try {
      const response = await apiService.get(
        `/system-settings/${encodeURIComponent(setting.setting_key)}`
      );
      hydrateConstantForm(response.data as SystemSetting);
    } catch (error) {
      console.error("Error loading system constant:", error);
      hydrateConstantForm(setting);
      setConstantFormError(
        error instanceof Error
          ? error.message
          : "Loaded cached row data. Could not refresh latest setting."
      );
    } finally {
      setConstantsSaving(false);
    }
  };

  const handleConstantTypeChange = (settingType: SettingType) => {
    const nextDrafts = {
      ...constantValueDrafts,
      [constantForm.setting_type]: constantForm.setting_value,
    };
    const nextValue =
      nextDrafts[settingType] ?? getDefaultValueForType(settingType);

    setConstantValueDrafts(nextDrafts);
    setConstantForm((current) => ({
      ...current,
      setting_type: settingType,
      setting_value: nextValue,
    }));
  };

 const serializeSettingValue = (form: SettingFormState) => {
  if (form.setting_type === "boolean") {
    return String(form.setting_value.toLowerCase() === "true");
  }

  if (form.setting_type === "number") {
    const value = Number(form.setting_value);

    if (!Number.isFinite(value)) {
      throw new Error("Setting value must be a valid number.");
    }

    return String(value); // <-- return string
  }

  if (form.setting_type === "json") {
    try {
      JSON.parse(form.setting_value); // validate JSON
      return form.setting_value;      // send as string
    } catch {
      throw new Error("Setting value must be valid JSON.");
    }
  }

  return String(form.setting_value);
};

  const submitConstant = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setConstantsSaving(true);
    setConstantFormError(null);

    try {
      if (!editingKey && !constantForm.setting_key.trim()) {
        throw new Error("Setting key is required.");
      }

      if (!constantForm.setting_name.trim()) {
        throw new Error("Setting name is required.");
      }

      if (
        constantForm.setting_type !== "boolean" &&
        !constantForm.setting_value.trim()
      ) {
        throw new Error("Setting value is required.");
      }

      const payload = {
        setting_name: constantForm.setting_name.trim(),
        description: constantForm.description.trim() || undefined,
        setting_type: constantForm.setting_type,
        setting_value: serializeSettingValue(constantForm),
        is_public: constantForm.is_public,
      };

      if (editingKey) {
        await apiService.put(
          `/system-settings/${encodeURIComponent(editingKey)}`,
          payload
        );
        setMessage({
          type: "success",
          text: "System constant updated successfully",
        });
      } else {
        await apiService.post("/system-settings/", {
          setting_key: constantForm.setting_key.trim(),
          ...payload,
        });
        setMessage({
          type: "success",
          text: "System constant added successfully",
        });
      }

      setTimeout(() => setMessage(null), 3000);
      setShowConstantModal(false);
      setEditingKey(null);
      setConstantForm(emptySettingForm);
      setConstantFormError(null);
      await loadSystemConstants();
    } catch (error) {
      console.error("Error saving system constant:", error);
      setConstantFormError(
        error instanceof Error
          ? error.message
          : "Failed to save system constant"
      );
    } finally {
      setConstantsSaving(false);
    }
  };

  const deleteConstant = async () => {
    if (!deleteTarget) {
      return;
    }

    setConstantsSaving(true);

    try {
      await apiService.delete(
        `/system-settings/${encodeURIComponent(deleteTarget.setting_key)}`
      );
      setMessage({
        type: "success",
        text: "System constant deleted successfully",
      });
      setTimeout(() => setMessage(null), 3000);
      setDeleteTarget(null);
      await loadSystemConstants();
    } catch (error) {
      console.error("Error deleting system constant:", error);
      setMessage({
        type: "error",
        text:
          error instanceof Error
            ? error.message
            : "Failed to delete system constant",
      });
    } finally {
      setConstantsSaving(false);
    }
  };

  const formatContentForView = (setting: SystemSetting) => {
    if (setting.setting_type === "json") {
      return typeof setting.setting_value === "string"
        ? setting.setting_value
        : JSON.stringify(setting.setting_value ?? {}, null, 2);
    }

    return String(setting.setting_value ?? "");
  };

  const closeConstantModal = () => {
    setShowConstantModal(false);
    setConstantFormError(null);
  };

  const applySystemFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSystemCurrentPage(1);
    setSystemSearchTerm(systemSearchInput.trim());
  };

  const clearSystemFilters = () => {
    setSystemSearchInput("");
    setSystemSearchTerm("");
    setSystemTypeFilter("");
    setSystemVisibilityFilter("");
    setSystemCurrentPage(1);
  };

  const hasSystemFilters = Boolean(
    systemSearchInput.trim() ||
      systemSearchTerm ||
      systemTypeFilter ||
      systemVisibilityFilter
  );

  const renderValueInput = () => {
    if (constantForm.setting_type === "boolean") {
      return (
        <select
          value={constantForm.setting_value.toLowerCase()}
          onChange={(event) =>
            setConstantForm((current) => ({
              ...current,
              setting_value: event.target.value,
            }))
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="true">True</option>
          <option value="false">False</option>
        </select>
      );
    }

    if (constantForm.setting_type === "number") {
      return (
        <input
          type="number"
          required
          value={constantForm.setting_value}
          onChange={(event) =>
            setConstantForm((current) => ({
              ...current,
              setting_value: event.target.value,
            }))
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      );
    }

    if (
      constantForm.setting_type === "html" ||
      constantForm.setting_type === "json"
    ) {
      return (
        <textarea
          required
          rows={constantForm.setting_type === "json" ? 8 : 6}
          value={constantForm.setting_value}
          onChange={(event) =>
            setConstantForm((current) => ({
              ...current,
              setting_value: event.target.value,
            }))
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
        />
      );
    }

    return (
      <input
        type="text"
        required
        value={constantForm.setting_value}
        onChange={(event) =>
          setConstantForm((current) => ({
            ...current,
            setting_value: event.target.value,
          }))
        }
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
    );
  };

  const renderTableValue = (setting: SystemSetting) => {
    if (setting.setting_type === "html" || setting.setting_type === "json") {
      return (
        <button
          type="button"
          onClick={() => setViewingSetting(setting)}
          className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800"
        >
          <Eye className="h-4 w-4" />
          View Content
        </button>
      );
    }

    if (setting.setting_type === "boolean") {
      const booleanValue = getBooleanValue(setting.setting_value) ?? false;

      return (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            booleanValue
              ? "bg-green-100 text-green-800"
              : "bg-gray-100 text-red-700"
          }`}
        >
          {booleanValue ? "True" : "False"}
        </span>
      );
    }

    return (
      <span className="block max-w-xs truncate">
        {String(setting.setting_value ?? "")}
      </span>
    );
  };

  const previewContent = () => {
    const content =
      activeTab === "terms"
        ? termsContent
        : activeTab === "privacy"
        ? privacyContent
        : aboutContent;
    const title =
      activeTab === "terms"
        ? "Terms & Conditions"
        : activeTab === "privacy"
        ? "Privacy Policy"
        : "About Content";
    const newWindow = window.open("", "_blank");
    if (newWindow) {
      newWindow.document.write(`
        <html>
          <head>
            <title>${title} Preview</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                min-height: 100vh;
                padding: 40px;
                background: #f3f4f6;
                display: flex;
                justify-content: center;
                align-items: flex-start;
              }
              .preview-card {
                width: 100%;
                max-width: 900px;
                background: #ffffff;
                border-radius: 18px;
                box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
                padding: 32px;
                box-sizing: border-box;
              }
              h1 {
                color: #111827;
                border-bottom: 2px solid #e5e7eb;
                padding-bottom: 12px;
                margin-bottom: 24px;
              }
              h2 { color: #4b5563; margin-top: 30px; }
              p { margin-bottom: 15px; color: #374151; }
              ul { margin-bottom: 15px; color: #374151; }
              li { margin-bottom: 5px; }
            </style>
          </head>
          <body>
            <div class="preview-card">
              <h1>${title} Preview</h1>
              ${
                content ||
                `<p><em>No content available. Please add ${
                  activeTab === "terms"
                    ? "terms and conditions"
                    : activeTab === "privacy"
                    ? "privacy policy"
                    : "about"
                } content.</em></p>`
              }
            </div>
          </body>
        </html>
      `);
      newWindow.document.close();
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">
          Manage system settings, legal documents, and configurations
        </p>
      </div>

      {/* Message Display */}
      {message && (
        <div
          className={`mb-6 p-4 rounded-md ${
            message.type === "success"
              ? "bg-green-50 border border-green-200 text-green-700"
              : "bg-red-50 border border-red-200 text-red-700"
          }`}
        >
          <div className="flex">
            <div className="flex-shrink-0">
              {message.type === "success" ? (
                <svg
                  className="h-5 w-5 text-green-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="h-5 w-5 text-red-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{message.text}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow p-6">
        {activeTab === "terms" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Terms & Conditions
              </h2>
              {loading && (
                <div className="text-sm text-gray-500">Loading...</div>
              )}
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Terms & Conditions Content
                </label>
                <textarea
                  value={termsContent}
                  onChange={(e) => setTermsContent(e.target.value)}
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                  placeholder="Enter your terms and conditions here... You can use HTML formatting."
                  disabled={loading}
                />
                <p className="mt-2 text-xs text-gray-500">
                  You can use HTML formatting. This content will be displayed on
                  the public Terms & Conditions page.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={previewContent}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  disabled={loading || saving}
                >
                  Preview
                </button>
                <button
                  onClick={saveContent}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                  disabled={loading || saving}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "privacy" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Privacy Policy
              </h2>
              {loading && (
                <div className="text-sm text-gray-500">Loading...</div>
              )}
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Privacy Policy Content
                </label>
                <textarea
                  value={privacyContent}
                  onChange={(e) => setPrivacyContent(e.target.value)}
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                  placeholder="Enter your privacy policy here... You can use HTML formatting."
                  disabled={loading}
                />
                <p className="mt-2 text-xs text-gray-500">
                  You can use HTML formatting. This content will be displayed on
                  the public Privacy Policy page.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={previewContent}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  disabled={loading || saving}
                >
                  Preview
                </button>
                <button
                  onClick={saveContent}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                  disabled={loading || saving}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "about" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                About Content
              </h2>
              {loading && (
                <div className="text-sm text-gray-500">Loading...</div>
              )}
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  About Page Content
                </label>
                <textarea
                  value={aboutContent}
                  onChange={(e) => setAboutContent(e.target.value)}
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                  placeholder="Enter your about page content here... You can use HTML formatting."
                  disabled={loading}
                />
                <p className="mt-2 text-xs text-gray-500">
                  You can use HTML formatting. This content will be displayed on
                  the public About page.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={previewContent}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  disabled={loading || saving}
                >
                  Preview
                </button>
                <button
                  onClick={saveContent}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                  disabled={loading || saving}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "system" && (
          <div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-5">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  System settings
                </h2>
                <p className="text-sm text-gray-500">
                  Manage configurable system values.
                  {systemTotalCount > 0 && (
                    <span className="ml-1">
                      {systemTotalCount} matching
                      {systemTotalCount === 1 ? " setting" : " settings"}
                    </span>
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={openAddConstant}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                <Plus className="h-4 w-4" />
                Add system settings
              </button>
            </div>

            {constantsError && !showConstantModal && (
              <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {constantsError}
              </div>
            )}

            <form
              onSubmit={applySystemFilters}
              className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4"
            >
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_180px_180px_auto_auto]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={systemSearchInput}
                    onChange={(event) =>
                      setSystemSearchInput(event.target.value)
                    }
                    placeholder="Search key, name, or description"
                    className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <select
                  value={systemTypeFilter}
                  onChange={(event) => {
                    setSystemTypeFilter(event.target.value);
                    setSystemCurrentPage(1);
                  }}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All types</option>
                  {settingTypes.map((settingType) => (
                    <option key={settingType} value={settingType}>
                      {settingType}
                    </option>
                  ))}
                </select>
                <select
                  value={systemVisibilityFilter}
                  onChange={(event) => {
                    setSystemVisibilityFilter(event.target.value);
                    setSystemCurrentPage(1);
                  }}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All visibility</option>
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                >
                  <Search className="h-4 w-4" />
                  Search
                </button>
                {hasSystemFilters && (
                  <button
                    type="button"
                    onClick={clearSystemFilters}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <X className="h-4 w-4" />
                    Clear
                  </button>
                )}
              </div>
            </form>

            <div className="overflow-hidden rounded-lg border border-gray-200">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Key
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Value
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Public
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {constantsLoading ? (
                      <tr>
                        <td
                          className="px-4 py-8 text-center text-sm text-gray-500"
                          colSpan={7}
                        >
                          Loading system settings...
                        </td>
                      </tr>
                    ) : systemConstants.length === 0 ? (
                      <tr>
                        <td
                          className="px-4 py-8 text-center text-sm text-gray-500"
                          colSpan={7}
                        >
                          {hasSystemFilters
                            ? "No system settings match the current filters."
                            : "No system settings found."}
                        </td>
                      </tr>
                    ) : (
                      systemConstants.map((setting) => (
                        <tr
                          key={setting.setting_key}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-4 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                            {setting.setting_key}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">
                            {setting.setting_name}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            <span className="block max-w-xs truncate">
                              {setting.description || "-"}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700">
                            {renderTableValue(setting)}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">
                            {setting.setting_type}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">
                            {setting.is_public ? "Yes" : "No"}
                          </td>
                          <td className="px-4 py-4 text-right text-sm font-medium whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => openEditConstant(setting)}
                              className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-900 mr-4"
                            >
                              <Edit className="h-4 w-4" />
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(setting)}
                              className="inline-flex items-center gap-1 text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {systemTotalCount > 0 && (
                <TablePagination
                  currentPage={systemCurrentPage}
                  totalCount={systemTotalCount}
                  rowsPerPage={systemRowsPerPage}
                  onPageChange={(page) => setSystemCurrentPage(page)}
                  onRowsPerPageChange={(rows) => {
                    setSystemRowsPerPage(rows);
                    setSystemCurrentPage(1);
                  }}
                />
              )}
            </div>
          </div>
        )}

        {activeTab === "email" && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Email Templates
            </h2>
            <p className="text-gray-500">
              Email template management coming soon...
            </p>
          </div>
        )}

        {activeTab === "integrations" && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Integrations
            </h2>
            <p className="text-gray-500">Integration settings coming soon...</p>
          </div>
        )}
      </div>

      {showConstantModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-70 p-4">
          <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingKey ? "Edit System Constant" : "Add System Constant"}
              </h3>
              <button
                type="button"
                onClick={closeConstantModal}
                className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={submitConstant} className="space-y-5 px-6 py-5">
              {constantFormError && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {constantFormError}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Setting Key *
                  </label>
                  <input
                    type="text"
                    required
                    disabled={Boolean(editingKey)}
                    value={constantForm.setting_key}
                    onChange={(event) =>
                      setConstantForm((current) => ({
                        ...current,
                        setting_key: event.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                    placeholder="id_verify_retry_count"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Setting Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={constantForm.setting_name}
                    onChange={(event) =>
                      setConstantForm((current) => ({
                        ...current,
                        setting_name: event.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Setting Type *
                  </label>
                  <select
                    required
                    value={constantForm.setting_type}
                    onChange={(event) =>
                      handleConstantTypeChange(
                        event.target.value as SettingType
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {settingTypes.map((settingType) => (
                      <option key={settingType} value={settingType}>
                        {settingType}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center pt-7">
                  <input
                    id="constant-is-public"
                    type="checkbox"
                    checked={constantForm.is_public}
                    onChange={(event) =>
                      setConstantForm((current) => ({
                        ...current,
                        is_public: event.target.checked,
                      }))
                    }
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label
                    htmlFor="constant-is-public"
                    className="ml-2 block text-sm text-gray-700"
                  >
                    Public setting
                  </label>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    value={constantForm.description}
                    onChange={(event) =>
                      setConstantForm((current) => ({
                        ...current,
                        description: event.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Setting Value *
                  </label>
                  {renderValueInput()}
                </div>
              </div>

              <div className="flex justify-end space-x-3 border-t border-gray-200 pt-5">
                <button
                  type="button"
                  onClick={closeConstantModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  disabled={constantsSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                  disabled={constantsSaving}
                >
                  {constantsSaving
                    ? "Saving..."
                    : editingKey
                    ? "Update Constant"
                    : "Add Constant"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewingSetting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-70 p-4">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {viewingSetting.setting_name}
              </h3>
              <button
                type="button"
                onClick={() => setViewingSetting(null)}
                className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto p-6">
              {viewingSetting.setting_type === "html" ? (
                <iframe
                  title={`${viewingSetting.setting_name} content`}
                  sandbox=""
                  srcDoc={formatContentForView(viewingSetting)}
                  className="h-96 w-full rounded-md border border-gray-200 bg-white"
                />
              ) : (
                <pre className="whitespace-pre-wrap rounded-md bg-gray-50 p-4 font-mono text-sm text-gray-800">
                  {formatContentForView(viewingSetting)}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={deleteConstant}
        title="Delete System Constant"
        message={`Delete "${
          deleteTarget?.setting_key || ""
        }"? This action cannot be undone.`}
        confirmText="Delete"
        type="danger"
        loading={constantsSaving}
      />
    </div>
  );
};

export default Settings;
